import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, WritableSignal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { EntityDetailTemplateComponent } from '@connect-services/client/components/entity-detail-template/entity-detail-template.component';
import {
    FieldInterventionEntityItemsListComponent,
    ItemWithState,
} from '@connect-services/client/components/field-intervention-entity-details/field-intervention-entity-items-list/field-intervention-entity-items-list.component';
import {
    AbstractEntityDetailComponent,
    AttributeValue,
} from '@connect-services/client/components/table-entity-detail/abstract-entity-detail.component';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { FeatureService } from '@connect-services/client/services/feature.service';
import { excludedAttributes } from '@connect-services/client/services/layer.service';
import { EntityLocation } from '@connect-services/client/services/map-draw.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { TableEditModeService } from '@connect-services/client/services/table-edit-mode.service';
import { ButtonType } from '@connect-services/src/lib/components/button/button.component';
import {
    EditAction,
    EntityState,
    FieldIntervention,
    FieldInterventionEntitiesService,
    FieldInterventionEntity,
    FieldInterventionForm,
    FieldInterventionsService,
    Layer,
    LayerAttribute,
    LayerControllerGetEntities200ResponseAllOfItemsInner as DataEntityAdditionalProperties,
    Person,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        EntityDetailTemplateComponent,
        FieldInterventionEntityItemsListComponent,
        LoadingComponent,
        RessourcesModule,
    ],
    selector: 'app-entity-detail',
    templateUrl: './entity-detail.component.html',
})
export class EntityDetailComponent extends AbstractEntityDetailComponent {
    public readonly geomBboxEntity: WritableSignal<EntityLocation | null> = signal(null);

    protected readonly ButtonType = ButtonType;
    protected readonly featureService = inject(FeatureService);
    protected readonly fieldIntervention: WritableSignal<FieldIntervention | null> = signal(null);
    protected readonly fieldInterventionEntity: WritableSignal<FieldInterventionEntity | null> = signal(null);
    protected readonly formItemsWithState = computed<ItemWithState[]>(() =>
        (this.fieldInterventionEntity()?.forms ?? []).map((form: FieldInterventionForm): ItemWithState => ({
            item: form,
            updated: false,
        })),
    );
    protected readonly peopleItemsWithState = computed<ItemWithState[]>(() =>
        (this.fieldInterventionEntity()?.people ?? []).map((person: Person): ItemWithState => ({
            item: person,
            updated: false,
        })),
    );

    private readonly editModeStateService = inject(EditModeStateService);
    private readonly fieldInterventionEntitiesService = inject(FieldInterventionEntitiesService);
    private readonly fieldInterventionService = inject(FieldInterventionsService);
    private readonly isEditActiveForThisView = computed(() => {
        if (this.homeService.isExternalPage()) {
            return this.tableEditModeService.isExternalTableEditActive();
        }

        return this.projectEditModeService.isEditModeActive();
    });
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly tableEditModeService = inject(TableEditModeService);

    public constructor() {
        super();
        effect(async () => {
            if (!this.featureService.data()) {
                return;
            }

            this.editEntityDetailService.isEditMode();

            this.isLoading.set(true);
            this.geomBboxEntity.set(null);

            this.identifier = this.featureService.getFeatureIdentifier();

            if (this.featureService.fieldInterventionEntityId()) {
                await this.initFieldInterventionEntity();
            } else {
                await this.initLayerEntity();
            }

            this.editEntityDetailService.isEditMode.set(this.isEditActiveForThisView());

            if (this.layerEntity()?.uuid) {
                this.editEntityDetailService.entityId.set(this.layerEntity()!.uuid);
            }

            this.geomBboxEntity.set(this.getGeomBboxEntity());

            if (this.editEntityDetailService.isEditMode()) {
                await this.editEntityDetailService.initEditForm(
                    this.bookmarkedAttributes(),
                    this.notBookmarkedAttributes(),
                );
            }

            this.isLoading.set(false);
        });

        effect(() => {
            this.isOpticalPathAvailable.set(this.layer()?.isUsedInOpticalPath ?? false);
        });
    }

    public getFieldIntervention(fieldInterventionId: string): Promise<FieldIntervention> {
        return firstValueFrom(
            this.fieldInterventionService.fieldInterventionControllerGetOne({
                id: fieldInterventionId,
            }),
        );
    }

    public getFieldInterventionEntity(): Promise<FieldInterventionEntity> {
        return firstValueFrom(
            this.fieldInterventionEntitiesService.fieldInterventionEntityControllerGetOne({
                id: this.featureService.fieldInterventionEntityId()!,
            }),
        );
    }

    public getGeomBboxEntity(): EntityLocation | null {
        if (!this.layerEntity) {
            return null;
        }

        if (this.layerEntity()?.geom?.bbox && this.layerEntity()?.geom?.type) {
            return {
                bbox: this.layerEntity()!.geom!.bbox,
                type: this.layerEntity()!.geom!.type,
            };
        }

        return null;
    }

    public getLayer(): Promise<Layer> {
        return firstValueFrom(
            this.layerService.layerControllerGetOne({
                id: this.featureService.data()!.source,
            }),
        );
    }

    public getLayerBookmarks(): Promise<LayerAttribute[]> {
        return firstValueFrom(
            this.layerService.layerControllerGetAttributes({
                id: this.featureService.data()!.source,
            }),
        );
    }

    public getLayerEntity(): Promise<DataEntityAdditionalProperties> {
        const entityState = this.editModeStateService.getEntityState(
            this.projectBloc.value()!.id,
            this.featureService.data()!.source,
            this.featureService.data()!.properties['uuid'],
        );

        if (entityState?.action === EditAction.CREATE && this.layer()?.deposit) {
            const dataEntity = this.buildDataEntityFromEntityState(entityState);

            return new Promise((resolve) => resolve(dataEntity));
        }

        return firstValueFrom(
            this.layerService.layerControllerGetLayersEntity({
                entityId: this.featureService.data()!.properties['uuid'],
                id: this.featureService.data()!.source,
            }),
        );
    }

    public async goToPreviousPage(): Promise<void> {
        this.featureService.resetData();

        const path = [RoutePathEnum.PROJECTS, this.projectBloc.value()?.id];
        await this.router.navigate(path);
    }

    protected centerMapOnEntityAndZoomIn(): void {
        const layer: Layer | null = this.layer();
        if (!layer) {
            return;
        }

        this.mapDrawService.centerMapOnEntityAndZoomIn(layer.id, this.geomBboxEntity(), this.layerEntity()?.geom);
    }

    protected async initLayerEntity(): Promise<void> {
        const layer = await this.getLayer();
        this.layer.set(layer);
        this.editEntityDetailService.layer.set(layer);

        this.editEntityDetailService.setBusinessIdentifierInfo();

        this.layer()?.type === Layer.TypeEnum.WFS ? this.setDataAttributesWfs() : await this.setLayerDataAttributes();

        await this.setIsFromAStandardizedDeposit();
    }

    protected async setLayerDataAttributes(): Promise<void> {
        if (!this.featureService.fieldInterventionEntityId()) {
            const layerEntity = await this.getLayerEntity();
            this.layerEntity.set(layerEntity);
            this.editEntityDetailService.entityId.set(layerEntity.uuid);
        }

        const layerBookmark = await this.getLayerBookmarks();
        const bookmarks = new Map(layerBookmark.map((bookmark: LayerAttribute) => [bookmark.column, bookmark]));

        const [bookmarked, notBookmarked] = Object.entries(
            this.fieldInterventionEntity() !== null
                ? this.fieldInterventionEntity()!.dataAttributes
                : this.layerEntity()!,
        ).reduce<[AttributeValue[], AttributeValue[]]>(
            ([bookmarked, notBookmarked], [key, value]) => {
                const bookmark = bookmarks.get(key);

                if (this.isEntityExcluded(key, value)) {
                    return [bookmarked, notBookmarked];
                }

                const attributeValue: AttributeValue = {
                    key: key,
                    order: bookmark?.order,
                    value: value,
                };

                return bookmark?.isBookmarked
                    ? [[...bookmarked, attributeValue], notBookmarked]
                    : [bookmarked, [...notBookmarked, attributeValue]];
            },
            [[], []],
        );

        this.setAttributes(bookmarked, notBookmarked);
    }

    private buildDataEntityFromEntityState(entityState: EntityState): DataEntityAdditionalProperties {
        AssertService.hasKey(entityState.businessIdentifier, 'key');
        AssertService.isString(entityState.businessIdentifier['key']);

        return {
            businessIdentifier: entityState.properties?.[entityState.businessIdentifier['key']],
            deposit_id: this.layer()!.deposit!,
            uuid: this.featureService.data()!.properties['uuid'],
            ...entityState.properties,
        };
    }

    private async initFieldInterventionEntity(): Promise<void> {
        this.fieldInterventionEntity.set(await this.getFieldInterventionEntity());
        this.fieldIntervention.set(await this.getFieldIntervention(this.fieldInterventionEntity()!.fieldIntervention));

        await this.setLayerDataAttributes();
    }

    private setDataAttributesWfs(): void {
        const attributeValues: AttributeValue[] = Object.entries(this.featureService.data()!.properties)
            .filter(([key, value]) => !excludedAttributes.includes(key) && value !== null && value !== undefined)
            .map(([key, value]) => ({
                key: key,
                value: value,
            }));

        attributeValues.filter((attr) => attr.key !== 'zn_uuid').sort((a, b) => a.key.localeCompare(b.key));

        this.bookmarkedAttributes.set([]);
        this.notBookmarkedAttributes.set(attributeValues);
    }
}
